// COP Copilot runtime — the wire/protocol types (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// LIFTED WHOLESALE from takbot services/agent-runtime (the R1-AGENT-RUNTIME protocol):
// packages/protocol/src/wire/runtime.ts + the McpManifest/McpTool/ScopToolMeta shapes from
// packages/protocol/src/vendor/introspect/introspect.ts. takbot imported these from the
// @takbot/protocol package; the COP is not a takbot workspace, so the wire types are vendored
// here UNCHANGED so the lifted loop/prompt/context/backends compile verbatim against them.
//
// This is the SAME contract takbot's iframe<->runtime protocol uses: the model emits ONE
// {capability,reason,args}|{final} object per step; the runtime feeds it numbered [n] tool-result
// previews; the FinalAnswer carries the TDF roll-up high-water-mark. The COP runs the loop
// IN-PROCESS (no iframe HTTP hop), but the type surface is identical to takbot's so the move is
// recognisably the same infra.
//
// Holds NO React, never writes the engine, no Node-isms.

// ─── MCP manifest (lifted from introspect.ts McpManifest/McpTool/ScopToolMeta) ────────────

/** Action-risk tier (takbot RiskTier). */
export type RiskTier = "read" | "reversible" | "external" | "irreversible";
/** Actor classes (takbot ActorClass). */
export type ActorClass = "human" | "npe" | "ai-agent";

/** Minimal JSON-Schema object shape (takbot JsonSchemaObject). */
export interface JsonSchemaObject {
  type: "object";
  properties?: Record<string, unknown>;
  required?: string[];
  additionalProperties?: boolean;
  description?: string;
}

/** COP-internal facts attached to each emitted tool (takbot ScopToolMeta). */
export interface ScopToolMeta {
  risk: RiskTier;
  actors: ActorClass[];
  action?: string;
  humanGated: boolean;
}

/** One MCP tool (takbot McpTool). The runtime feeds the model `name` + `description` +
 *  `inputSchema`; `_scop` carries the COP-internal risk/actor facts the prompt surfaces. */
export interface McpTool {
  name: string;
  description: string;
  inputSchema: JsonSchemaObject;
  _scop: ScopToolMeta;
}

/** The full MCP tool manifest (takbot McpManifest). */
export interface McpManifest {
  $schema: "scop.mcp.tools/1";
  tools: McpTool[];
}

/** The data-catalog the host attaches so the model gets valid zone ids + categorical VALUES
 *  IN CONTEXT (no table.manifest round-trip). Lifted from takbot TableCatalog. The COP derives
 *  this from the VISIBLE working set only (Glomar — copilotTables.ts manifest()). */
export interface TableCatalog {
  zones_catalog: { id: string; name: string; zone_type: string | null }[];
  entity_values: Record<string, string[]>;
  hint?: string;
  notes?: string[];
}

/** An MCP manifest enriched with the data catalog (takbot TakbotManifest). */
export type TakbotManifest = McpManifest & { catalog?: TableCatalog };

// ─── runtime wire types (lifted verbatim from wire/runtime.ts) ────────────────────────────

/** Which LLM backend the runtime is using. `mock` is the zero-dependency default; `openai`
 *  is TDFBot's preserved /v1 path. */
export type RuntimeBackend = "mock" | "openai";

/** The context-window configuration — TDFBot's char-budgeted knobs, preserved verbatim. */
export interface ContextWindowConfig {
  ctx_budget_chars: number; // default 20000
  doc_trunc_chars: number; // default 2400
  top_k: number; // default 10
  temperature: number; // default 0.4
  seed: number; // default 42
  max_tokens: number; // default 4096
  system_prompt_id: string;
  /** THE TOGGLE. "think" = two-phase (free reason → constrained emit); "instant" = one
   *  grammar-constrained call. */
  reasoning_mode: "think" | "instant";
  /** Loop-safety cap: max model steps before the runtime forces a grounded-but-LIMITED final. */
  max_steps: number; // default 6
}

/** A single tool the model chose to invoke. */
export interface ToolCall {
  call_id: string;
  /** A capability id from the manifest, e.g. "table.query" | "maps.set_viewport". */
  capability: string;
  reason: string;
  args: unknown;
}

/** The outcome of running a ToolCall (over the confined working set, in-process). */
export interface ToolResult {
  call_id: string;
  ok: boolean;
  /** On ok: the result preview + provenance + rollup (the cleared-rows preview ONLY). */
  result?: unknown;
  /** On failure: a Glomar-safe error code + message. */
  error?: { code: string; message: string; details?: unknown };
}

export interface Citation {
  ref: string;
  index: number;
  label?: string;
}

export interface FinalAnswer {
  text: string;
  citations: Citation[];
  /** The session high-water-mark marking the answer inherits (TDF roll-up banner). */
  marking?: string;
  coverage_warning?: string;
}

/** The runtime's response to a turn step — either more tool calls to run, or the final answer. */
export interface AgentStep {
  turn_id: string;
  status: "tool_calls" | "final";
  tool_calls?: ToolCall[];
  final?: FinalAnswer;
  trace?: string;
}

export interface AgentTurnRequest {
  session_id: string;
  user_text: string;
  manifest: McpManifest;
}

export interface AgentContinueRequest {
  session_id: string;
  turn_id: string;
  tool_results: ToolResult[];
}

/** The default context config — lifted from takbot DEFAULT_CONTEXT_CONFIG (the eval-EARNED
 *  defaults: reasoning_mode "instant", max_steps 6 — instant won the Qwen3.6 bench). */
export const DEFAULT_CONTEXT_CONFIG: ContextWindowConfig = {
  ctx_budget_chars: 20_000,
  doc_trunc_chars: 2_400,
  top_k: 10,
  temperature: 0.4,
  seed: 42,
  max_tokens: 4_096,
  system_prompt_id: "all-source-analyst.v1",
  reasoning_mode: "instant",
  max_steps: 6,
};

/** The canonical COP-copilot capability ids the model may call (the COP analog of takbot CAP).
 *  These are the tool NAMES the manifest advertises and the model emits as `capability`. */
export const CAP = {
  TABLE_MANIFEST: "table.manifest",
  TABLE_QUERY: "table.query",
  MAPS_HIGHLIGHT_ROWS: "maps.highlight_rows",
  MAPS_CLEAR_HIGHLIGHTS: "maps.clear_highlights",
  MAPS_SET_VIEWPORT: "maps.set_viewport",
  MAPS_FILTER_LAYER: "maps.filter_layer",
  MAPS_REQUEST_EVAC: "maps.request_evac",
} as const;
export type CapabilityId = (typeof CAP)[keyof typeof CAP];
